// src/components/SettingsDropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  Home, 
  User, 
  Building2, 
  Briefcase, 
  MapPin, 
  Database, 
  Headphones, 
  CreditCard, 
  LogOut,
  ChevronRight,
  UserCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export type SettingsTab = 'company' | 'office' | 'department' | 'designation' | 'terms-and-conditions' | 'startupkhata';

interface SettingsDropdownProps {
  onSelectSetting: (tab: SettingsTab) => void;
  onLogout?: () => void;
}

export function SettingsDropdown({ onSelectSetting, onLogout }: SettingsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    if (onLogout) {
      onLogout();
    } else {
      logout();
    }
  };

  const navigate = useNavigate();
  const role = user?.role || "admin";

// const filteredSettings = settingsOptions.filter(option =>
//   option.roles.includes(role)
// );

  const settingsOptions = [
  { id: 'company' as const, label: 'Company', icon: Briefcase, roles: ['admin','founder','director', 'hr head', 'manager'] },
  { id: 'office' as const, label: 'Office', icon: MapPin, roles: ['admin','founder','director','employee', 'hr head', 'manager', 'supervisor'] },
  { id: 'department' as const, label: 'Department', icon: Building2, roles: ['admin','founder','director','hr head', 'manager'] },
  { id: 'designation' as const, label: 'Designation', icon: UserCircle, roles: ['admin','founder','director','hr head', 'manager'] },
  { id: 'terms-and-conditions' as const, label: 'Terms & Conditions', icon: Settings, roles: ['admin','founder','director','hr head', 'manager'] },
  { id: 'startupkhata' as const, label: 'Startup Khata', icon: Database, roles: ['admin','founder','director', 'hr head', 'manager'] },
];
const filteredSettings = settingsOptions.filter(option =>
  option.roles.includes(role)
);
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Settings Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
//         onClick={() => {
//           navigate(`/settings/${setting.id}`);
//           setIsOpen(false);
// }}
        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors relative group"
        title="Settings"
      >
        <Settings size={22} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* User Info Header */}
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2 max-h-96 overflow-y-auto">
            {filteredSettings.map((option, index) => (
              <React.Fragment key={option.id}>
                <button
                  onClick={() => {
                    // onSelectSetting(option.id);
                    navigate(`/settings/${option.id}`);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group
                    ${option.id === 'upgrade' ? 'text-orange-600 hover:bg-orange-50' : 'text-gray-700'}
                  `}
                >
                  <div className={`
                    p-1.5 rounded-lg transition-colors
                    ${option.id === 'upgrade' 
                      ? 'text-orange-500 group-hover:text-orange-600' 
                      : 'text-gray-500 group-hover:text-blue-600'
                    }
                  `}>
                    <option.icon size={18} />
                  </div>
                  <span className="text-sm font-medium flex-1">{option.label}</span>
                  <ChevronRight size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                {option.divider && index < settingsOptions.length - 1 && (
                  <div className="my-1 border-t border-gray-100" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-100 py-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors text-left text-red-600 group"
            >
              <div className="p-1.5 rounded-lg text-red-500 group-hover:text-red-600">
                <LogOut size={18} />
              </div>
              <span className="text-sm font-medium flex-1">Logout</span>
              <ChevronRight size={14} className="text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInFromTop {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-in {
          animation-duration: 0.2s;
          animation-fill-mode: both;
        }
        
        .fade-in {
          animation-name: fadeIn;
        }
        
        .slide-in-from-top-2 {
          animation-name: slideInFromTop;
        }
      `}</style>
    </div>
  );
}